import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api";
import { Globe, Phone, Mail, Wallet, ExternalLink } from "lucide-react";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handle = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const data = await login(email, password);
            navigate(
                data.role === "super_admin" ? "/admin"
                : data.role === "teacher" ? "/teacher"
                : data.role === "secretary" ? "/secretary"
                : data.role === "quality_coordinator" ? "/quality"
                : data.role === "student" ? "/student"
                : "/parent"
            );
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col paper-bg">
            <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
                <div className="w-full max-w-md">
                    {/* Logo + School name */}
                    <div className="flex flex-col items-center text-center mb-8">
                        <img src="/gg-logo.png" alt="German Gymnasium" className="w-24 h-24 sm:w-28 sm:h-28 object-contain mb-4" data-testid="login-logo" />
                        <h1 className="font-heading text-2xl sm:text-3xl font-black text-oxford tracking-tight">German Gymnasium</h1>
                        <div className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-slate-500 mt-1">Platformë Notash • Prishtinë</div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8">
                        <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 mb-1 text-center">Hyrje</h2>
                        <p className="text-sm text-slate-600 text-center mb-6">Identifikohu për të parë notat dhe detyrat.</p>

                        <form onSubmit={handle} className="space-y-4" data-testid="login-form">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">Emaili juaj</label>
                                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="emaili.juaj@gmail.com" data-testid="login-email-input" className="h-11 bg-white" />
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Fjalëkalimi</label>
                                    <Link to="/forgot-password" className="text-xs text-oxford hover:underline font-semibold" data-testid="login-forgot-link">Keni harruar?</Link>
                                </div>
                                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" data-testid="login-password-input" className="h-11 bg-white" />
                            </div>
                            {error && (
                                <div className="text-sm text-terracotta bg-terracotta/10 border border-terracotta/30 rounded-lg px-4 py-3" data-testid="login-error">
                                    {error}
                                </div>
                            )}
                            <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full h-11 bg-oxford hover:bg-oxfordhover text-white font-semibold">
                                {loading ? "Duke hyrë…" : "Hyni"}
                            </Button>
                        </form>

                        {/* Financa quick link — visible to anyone, no login required */}
                        <a
                            href="https://ggfinanca.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="login-financa-btn"
                            className="mt-5 group flex items-center justify-center gap-2.5 w-full h-12 rounded-xl bg-gradient-to-r from-sage to-sage/90 hover:from-sage/95 hover:to-sage text-white font-bold text-sm shadow-md hover:shadow-lg transition"
                        >
                            <Wallet className="w-5 h-5" />
                            <span className="tracking-wide">Financat</span>
                            <ExternalLink className="w-3.5 h-3.5 opacity-80 group-hover:translate-x-0.5 transition" />
                        </a>
                        <p className="text-[10px] text-slate-500 text-center mt-2">Hyni në sistemin e financave të shkollës</p>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="text-center text-xs text-slate-600 py-6 px-4 border-t border-slate-200/60 bg-white/60">
                <div className="font-heading font-bold text-oxford text-sm mb-2">German Gymnasium</div>
                <div className="flex flex-col sm:flex-row sm:justify-center gap-2 sm:gap-6">
                    <a href="https://www.gg-ks.com" target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1.5 hover:text-oxford" data-testid="footer-web">
                        <Globe className="w-3.5 h-3.5" /> www.gg-ks.com
                    </a>
                    <a href="tel:049986700" className="inline-flex items-center justify-center gap-1.5 hover:text-oxford" data-testid="footer-tel">
                        <Phone className="w-3.5 h-3.5" /> 049 986 700
                    </a>
                    <a href="mailto:info@gg-ks.com" className="inline-flex items-center justify-center gap-1.5 hover:text-oxford" data-testid="footer-email">
                        <Mail className="w-3.5 h-3.5" /> info@gg-ks.com
                    </a>
                </div>
            </footer>
        </div>
    );
}
